import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import NewPostModal from '../components/NewPostModal';
import RiskLight from '../components/RiskLight';
import { LEAD_STATUS_BADGE, LEAD_STATUS_LABEL, PLATFORM_LABEL, formatDateTime } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || '';
const STATUSES = ['novo', 'investigando', 'promovido', 'descartado'];

function Leads({ user, onLogout }) {
  const [status, setStatus] = useState('novo');
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    const { data } = await axios.get(`${API_URL}/api/leads`, { params: status ? { status_filter: status } : {} });
    setLeads(data.leads || []);
    setTotal(data.total || 0);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const setLeadStatus = async (lead, newStatus) => {
    await axios.patch(`${API_URL}/api/leads/${lead.id}`, { status: newStatus }, { headers });
    load();
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Leads de redes sociais</h1>
          <p className="text-sm text-slate-500">
            Perfis e posts citados em notícias relevantes, prontos para um educador investigar e promover.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium ring-1 ring-inset ${
                status === s ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >
              {LEAD_STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-slate-400">Carregando…</p>
        ) : leads.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">
            Nenhum lead com esse status. Colete notícias na aba{' '}
            <button className="text-brand-600 underline" onClick={() => navigate('/news')}>Notícias</button> para gerar novos.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {leads.map((lead) => (
              <div key={lead.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-slate-800">
                        {PLATFORM_LABEL[lead.platform] || lead.platform || 'Rede desconhecida'} · @{lead.handle_or_name}
                      </p>
                      <RiskLight rating={lead.rating} />
                    </div>
                    {lead.rating?.reason && <p className="mt-0.5 text-xs text-slate-500">{lead.rating.reason}</p>}
                    {lead.article_title && (
                      <button
                        onClick={() => navigate(`/news`)}
                        className="mt-0.5 line-clamp-1 text-left text-xs text-slate-500 hover:text-brand-600 hover:underline"
                      >
                        via: {lead.article_title}
                      </button>
                    )}
                    {lead.context && <p className="mt-1 text-xs italic text-slate-400">“{lead.context}”</p>}
                    <p className="mt-1 text-xs text-slate-300">{formatDateTime(lead.created_at)}</p>
                  </div>

                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${LEAD_STATUS_BADGE[lead.status]}`}>
                      {LEAD_STATUS_LABEL[lead.status]}
                    </span>
                    {lead.status === 'novo' && (
                      <button
                        onClick={() => setLeadStatus(lead, 'investigando')}
                        className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"
                      >
                        Investigar
                      </button>
                    )}
                    {(lead.status === 'novo' || lead.status === 'investigando') && (
                      <>
                        <button
                          onClick={() => setPromoting(lead)}
                          className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
                        >
                          Promover a post
                        </button>
                        <button
                          onClick={() => setLeadStatus(lead, 'descartado')}
                          className="rounded-lg px-2 py-1.5 text-xs font-medium text-slate-400 hover:bg-slate-100"
                        >
                          Descartar
                        </button>
                      </>
                    )}
                    {lead.promoted_post_id && (
                      <button
                        onClick={() => navigate(`/posts/${lead.promoted_post_id}`)}
                        className="text-xs font-medium text-brand-600 hover:underline"
                      >
                        Ver post →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <p className="text-center text-xs text-slate-400">{leads.length} de {total}</p>
          </div>
        )}
      </div>

      {promoting && (
        <NewPostModal
          endpoint={`/api/leads/${promoting.id}/promote`}
          submitLabel="Promover a post"
          initial={{ content: '', source_url: '' }}
          onClose={() => setPromoting(null)}
          onCreated={() => { setPromoting(null); load(); }}
        />
      )}
    </Layout>
  );
}

export default Leads;
